import { Fragment, useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';
import { supabase } from '@/lib/supabase';
import Stars from '@/pages/product-detail/components/Stars';
import AdUnit from '@/components/AdUnit';
import { usePageMeta } from '@/hooks/usePageMeta';

const LEADERBOARD_SLOT = import.meta.env.VITE_AD_SLOT_LEADERBOARD as string | undefined;
const INCONTENT_SLOT = import.meta.env.VITE_AD_SLOT_INCONTENT as string | undefined;

interface Product {
  id: string;
  name: string;
  brand: string;
  rating: number;
  reviews_count: number;
  description: string;
  price: number;
  original_price: number | null;
  discount: number;
  image: string;
  badge: string;
  category: string;
}

interface Category {
  id: string;
  slug: string;
  name: string;
  icon: string;
  color: string;
  count: number;
}

export default function Search() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const categoryParam = searchParams.get('category') || '';
  const [sort, setSort] = useState<'relevance' | 'rating' | 'price-asc' | 'price-desc'>('relevance');
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const [prodRes, catRes] = await Promise.all([
        supabase.from('products').select('*'),
        supabase.from('categories').select('*').order('sort_order'),
      ]);
      if (prodRes.data) setAllProducts(prodRes.data);
      if (catRes.data) setCategories(catRes.data);
      setLoading(false);
    };
    loadData();
  }, []);

  const catLabel = categoryParam
    ? categories.find(c => c.slug === categoryParam)?.name || categoryParam
    : '';
  usePageMeta(
    query ? `"${query}" Search Results` : catLabel ? `${catLabel} Products` : 'Search Products',
    query
      ? `Search results for "${query}": real product reviews and prices on BuyWize.`
      : catLabel
        ? `Browse ${catLabel} products with real reviews, ratings, and prices.`
        : 'Search real product reviews, ratings, and prices on BuyWize.'
  );

  const results = useMemo(() => {
    let filtered = allProducts;
    if (query) {
      const q = query.toLowerCase();
      filtered = filtered.filter(
        p =>
          p.name.toLowerCase().includes(q) ||
          p.brand.toLowerCase().includes(q) ||
          (p.category && p.category.toLowerCase().includes(q)) ||
          p.description.toLowerCase().includes(q),
      );
    }
    if (categoryParam) {
      filtered = filtered.filter(p =>
        p.category &&
        (p.category.toLowerCase() === categoryParam.toLowerCase() ||
          p.category.toLowerCase().replace(/\s+/g, '-') === categoryParam.toLowerCase())
      );
    }
    switch (sort) {
      case 'rating':
        filtered = [...filtered].sort((a, b) => b.rating - a.rating);
        break;
      case 'price-asc':
        filtered = [...filtered].sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        filtered = [...filtered].sort((a, b) => b.price - a.price);
        break;
      default:
        break;
    }
    return filtered;
  }, [allProducts, query, categoryParam, sort]);

  const currentCategory = categoryParam
    ? categories.find(c => c.slug === categoryParam)
    : null;

  return (
    <div className="min-h-screen bg-white text-ink-900 antialiased">
      <Header />
      <main className="pt-28 md:pt-36 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Header */}
          <div className="mb-8">
            {currentCategory ? (
              <div>
                <nav className="flex items-center gap-2 text-sm text-ink-500 mb-4">
                  <Link to="/" className="hover:text-cta-600 transition whitespace-nowrap">Home</Link>
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-base"></i>
                  </span>
                  <span className="text-ink-800 font-medium">{currentCategory.name}</span>
                </nav>
                <div className="flex items-center gap-4">
                  <span className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${currentCategory.color} text-white flex items-center justify-center`}>
                    <i className={`${currentCategory.icon} text-2xl`}></i>
                  </span>
                  <div>
                    <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-900 tracking-tight">
                      {currentCategory.name}
                    </h1>
                    <p className="mt-1 text-ink-600">
                      {currentCategory.count}+ products reviewed &bull; {results.length} results
                    </p>
                  </div>
                </div>
              </div>
            ) : query ? (
              <div>
                <nav className="flex items-center gap-2 text-sm text-ink-500 mb-4">
                  <Link to="/" className="hover:text-cta-600 transition whitespace-nowrap">Home</Link>
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-base"></i>
                  </span>
                  <span className="text-ink-800 font-medium">Search</span>
                </nav>
                <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-900 tracking-tight">
                  Results for &ldquo;{query}&rdquo;
                </h1>
                <p className="mt-1 text-ink-600">{results.length} product{results.length !== 1 ? 's' : ''} found</p>
              </div>
            ) : (
              <div>
                <nav className="flex items-center gap-2 text-sm text-ink-500 mb-4">
                  <Link to="/" className="hover:text-cta-600 transition whitespace-nowrap">Home</Link>
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-s-line text-base"></i>
                  </span>
                  <span className="text-ink-800 font-medium">All Products</span>
                </nav>
                <h1 className="font-display text-3xl md:text-4xl font-bold text-ink-900 tracking-tight">
                  Browse All Products
                </h1>
                <p className="mt-1 text-ink-600">{results.length} product{results.length !== 1 ? 's' : ''} available</p>
              </div>
            )}
          </div>

          {/* Sort bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-1.5">
              {(['relevance', 'rating', 'price-asc', 'price-desc'] as const).map(s => (
                <button
                  key={s}
                  onClick={() => setSort(s)}
                  className={`h-9 px-4 rounded-full text-sm font-semibold whitespace-nowrap transition ${
                    sort === s
                      ? 'bg-ink-900 text-white'
                      : 'bg-ink-50 text-ink-600 hover:bg-ink-100'
                  }`}
                >
                  {s === 'relevance' ? 'Relevance' : s === 'rating' ? 'Top Rated' : s === 'price-asc' ? 'Price: Low' : 'Price: High'}
                </button>
              ))}
            </div>
            <span className="text-xs text-ink-500">{results.length} results</span>
          </div>

          {/* No results */}
          {results.length === 0 && (
            <div className="text-center py-20">
              <span className="w-16 h-16 rounded-2xl bg-ink-50 text-ink-400 flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-3xl"></i>
              </span>
              <h2 className="font-display text-2xl font-bold text-ink-900">No products found</h2>
              <p className="mt-2 text-ink-600 max-w-md mx-auto">
                Try adjusting your search or browse by category instead.
              </p>
              <Link
                to="/#categories"
                className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-cta-500 hover:bg-cta-600 text-white text-sm font-semibold whitespace-nowrap transition"
              >
                Browse Categories
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-base"></i>
                </span>
              </Link>
            </div>
          )}

          {/* Results grid */}
          {results.length > 0 && (
            <>
              <div className="mb-6">
                <AdUnit slot={LEADERBOARD_SLOT} label="Advertisement" style={{ minHeight: 90 }} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6" data-product-shop>
                {results.map((p, idx) => (
                  <Fragment key={p.id}>
                    <article
                      className="product-card group relative bg-white rounded-2xl border border-ink-100 overflow-hidden flex flex-col"
                    >
                      <Link to={`/product/${p.id}`} className="relative aspect-square bg-ink-50 overflow-hidden block">
                        <img
                          src={p.image}
                          alt={p.name}
                          className="product-image w-full h-full object-contain p-4"
                        />
                        {'discount' in p && p.discount && (
                          <span className="absolute top-3 left-3 h-6 px-2 rounded-md bg-cta-500 text-white text-[10px] font-bold uppercase tracking-wider flex items-center">
                            -{p.discount}%
                          </span>
                        )}
                        {p.badge && (
                          <span className="absolute top-3 right-3 h-6 px-2 rounded-md bg-white/90 backdrop-blur text-ink-800 text-[10px] font-bold uppercase tracking-wider flex items-center">
                            {p.badge}
                          </span>
                        )}
                      </Link>

                      <div className="p-5 flex flex-col flex-1">
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{p.brand}</p>
                        <Link
                          to={`/product/${p.id}`}
                          className="mt-1 font-semibold text-ink-900 text-sm leading-snug line-clamp-2 min-h-[40px] hover:text-cta-600 transition"
                        >
                          {p.name}
                        </Link>

                        <div className="mt-2 flex items-center gap-2">
                          <Stars rating={p.rating} />
                          <span className="text-xs font-semibold text-ink-800">{p.rating}</span>
                          <span className="text-xs text-ink-400">({(p as Product).reviews_count.toLocaleString()})</span>
                        </div>

                        <div className="mt-auto pt-4 flex items-end justify-between">
                          <div>
                            <p className="font-display text-xl font-bold text-ink-900">
                              ${p.price}
                              {p.original_price && (
                                <span className="ml-2 text-xs font-medium text-ink-400 line-through">${p.original_price}</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </article>
                    {(idx + 1) % 8 === 0 && idx < results.length - 1 && (
                      <div className="col-span-full">
                        <AdUnit slot={INCONTENT_SLOT} label="Advertisement" style={{ minHeight: 90 }} />
                      </div>
                    )}
                  </Fragment>
                ))}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}